var _class, _descriptor, _descriptor2, _descriptor3, _descriptor4;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { SchemaGenerator } from './config/schema-generator';
import State from './composer/state';
import Commands from './composer/commands';
import UploadManager from './composer/upload-manager';
import Editor from './composer/editor';
import { hoverInfo } from './helpers/hover';
import AttachmentList from './composer/attachment-list';
import { guidFor } from '@ember/object/internals';
import CreateTicket from './composer/create-ticket';
class NoOpAnalytics {
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  trackAnalyticsEvent() {}
}

/**
 * The Composer object is the core of this addon. It manages all the state for an instance of the composer
 * and provides methods for updating that state programmatically. It is exposed by the composer component
 * and forms the main API for updating the composer both internally to this addon and in the consuming
 * ember app via the component API.
 */
let Composer = (_class = class Composer {
  constructor(doc, conf, onChange, element, onFocus, onBlur, previousDocument) {
    _initializerDefineProperty(this, "state", _descriptor, this);
    _defineProperty(this, "commands", void 0);
    _defineProperty(this, "config", void 0);
    _defineProperty(this, "schema", void 0);
    _defineProperty(this, "blocksDoc", void 0);
    _defineProperty(this, "previousDocument", void 0);
    _defineProperty(this, "identifier", void 0);
    _initializerDefineProperty(this, "attachmentList", _descriptor2, this);
    _initializerDefineProperty(this, "insertTicketCardBlock", _descriptor3, this);
    _defineProperty(this, "editor", void 0);
    _defineProperty(this, "isDestroying", false);
    _defineProperty(this, "onChange", void 0);
    _defineProperty(this, "onFocus", void 0);
    _defineProperty(this, "onBlur", void 0);
    _initializerDefineProperty(this, "anchorInsertionMarker", _descriptor4, this);
    this.blocksDoc = doc;
    this.config = conf;
    this.onChange = onChange;
    this.onFocus = onFocus;
    this.onBlur = onBlur;
    this.schema = new SchemaGenerator(this.config).schema();
    this.editor = this.initEditorView(element, conf.analytics || new NoOpAnalytics());
    this.attachmentList = this.initAttachmentList();
    this.insertTicketCardBlock = this.initCreateTicketBlock();
    this.identifier = guidFor(this);
    let {
      state,
      commands
    } = this.setupStateAndCommands();
    this.state = state;
    this.commands = commands;
    if (this.config.autoFocus) {
      this.commands.focus();
    }
    if (this.config.initialSelectionLocation === 'end') {
      this.commands.setSelectionToEnd();
    }
    this.updateSelectionInfo();
    if (this.config.isInteractivityDisabled) {
      this.disableInteractivity();
    }
    if (previousDocument !== undefined) {
      this.commands.diffFrom(previousDocument, this.blocksDoc.stepsSinceLastChange);
    }
  }
  initEditorView(elem, analytics) {
    return new Editor({
      elem,
      composer: this,
      onChange: (newState, docChanged) => {
        if (this.state) {
          this.state.set(newState);
          this.updateSelectionInfo();
          this.updateTypeaheadState();
          if (docChanged) {
            this.state.isEditing = true;
            this.outputNewBlocksDoc();
          }
        }
      },
      analytics
    });
  }
  initAttachmentList() {
    if (this.config.upload?.allowedAttachmentTypes) {
      return new AttachmentList(this);
    }
    return;
  }
  initCreateTicketBlock() {
    return new CreateTicket(this);
  }
  loadNewBlocks(newBlocks) {
    if (newBlocks.equals(this.blocksDoc)) {
      return;
    }
    this.blocksDoc = newBlocks;
    this.editor.reloadBlocks();
    this.attachmentList?.destroy();
    this.insertTicketCardBlock?.destroy();
    this.attachmentList = this.initAttachmentList();
    this.insertTicketCardBlock = this.initCreateTicketBlock();
    let {
      state,
      commands
    } = this.setupStateAndCommands();
    this.state = state;
    this.commands = commands;
    this.updateSelectionInfo();
    this.updateTypeaheadState();
  }
  destroy() {
    this.isDestroying = true;
    this.editor.destroy();
    this.attachmentList?.destroy();
    this.state.isDestroying = true;
    this.commands.isDestroying = true;
  }
  setupStateAndCommands() {
    let uploadManager;
    if (this.config.upload) {
      uploadManager = new UploadManager(this.config);
    }
    let state = new State(this.editor.editorView, this.config, uploadManager);
    let commands = new Commands(this.editor.editorView, this.config, uploadManager, this.attachmentList, this.insertTicketCardBlock);
    return {
      state,
      commands
    };
  }
  updateHoverInfo(e) {
    this.state.updateHoverInfo(e, this.editor.editorView, this.identifier);
  }
  updateSelectionInfo() {
    this.state.updateSelectionInfo(this.editor.editorView);
  }
  updateTypeaheadState() {
    this.state.updateTypeaheadState(this.editor.editorView);
  }
  hoverInfo(e) {
    return hoverInfo(e, this.editor.editorView, this.identifier);
  }
  enableInteractivity() {
    this.state.isInteractive = true;
  }
  disableInteractivity() {
    this.state.isInteractive = false;
  }
  enableFormatting() {
    this.state.isFormattable = true;
  }
  disableFormatting() {
    this.state.isFormattable = false;
  }
  hideSectionType() {
    this.state.showSectionType = false;
  }
  scrollOrResizeHandler() {
    this.updateSelectionInfo();
    this.state.isEditing = true;
  }
  onFocusHandler() {
    if (this.onFocus) {
      this.onFocus();
    }
  }
  onBlurHandler() {
    if (this.onBlur) {
      this.onBlur();
    }
  }
  outputNewBlocksDoc() {
    if (!this.isDestroying) {
      this.blocksDoc = this.editor.doc.appendMultiple([this.attachmentList?.asBlock, this.insertTicketCardBlock.ticketBlock]);
      this.onChange(this.blocksDoc);
    }
  }
  findDomAtDocumentPosition(position) {
    return this.editor.editorView.domAtPos(position);
  }
  nodeDom(position) {
    return this.editor.editorView.nodeDOM(position);
  }
}, (_descriptor = _applyDecoratedDescriptor(_class.prototype, "state", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "attachmentList", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "insertTicketCardBlock", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "anchorInsertionMarker", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "scrollOrResizeHandler", [action], Object.getOwnPropertyDescriptor(_class.prototype, "scrollOrResizeHandler"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onFocusHandler", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onFocusHandler"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onBlurHandler", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onBlurHandler"), _class.prototype)), _class);
export { Composer as default };